import posthog from 'posthog-js'

const PROJECT_KEY = 'phc_Cc2xm7pRd5uwJGCWCwBR2EpuESq7mCJwhLY5xC8MjFPQ'
const API_HOST = 'https://eu.i.posthog.com'

const URL_PROPERTY = /url|pathname|referrer/i

const ID_PATH_SEGMENT =
  /\/(\d+|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})(?=\/|$|\?|#)/gi

const SAFE_QUERY_KEYS = new Set([
  'tab',
  'type',
  'page',
  'pageNumber',
  'pageSize',
  'sortingBy',
  'ordering',
  'dateFilter',
  'statusFilter',
  'reportType',
])

const QUERY_PAIR = /([?&])([^=&#]+)=([^&#]*)/g

const PENDO_FEATURE_ATTR = 'attr__data-pendo-feature'

// PostHog's 2026-05-30 defaults often omit $elements and send $elements_chain
// instead: `button:attr__data-pendo-feature="incidents-drawer-expand-toggle"nth-child="1";…`
const PENDO_FEATURE_IN_CHAIN = /(?:attr__)?data-pendo-feature="([A-Za-z0-9_-]+)"/

export const POSTHOG_FEATURE_SELECTOR = '[data-pendo-feature]'

export const POSTHOG_APP = 'spa'

// `environment` and `region` carry the account payload verbatim (PROD, US):
// saved insights filter on those exact strings.
export function posthogAppProperties({ deployment, region } = {}) {
  const properties = { app: POSTHOG_APP }
  if (typeof deployment === 'string' && deployment) properties.environment = deployment
  if (typeof region === 'string' && region) properties.region = region
  return properties
}

export const scrubIds = (value) =>
  value
    .replace(ID_PATH_SEGMENT, '/:id')
    .replace(QUERY_PAIR, (pair, separator, key) =>
      SAFE_QUERY_KEYS.has(key) ? pair : `${separator}${key}=redacted`
    )

export const getPendoFeature = (event) => {
  const elements = event?.properties?.$elements
  if (Array.isArray(elements)) {
    const fromElements = elements.find(
      (el) => typeof el?.[PENDO_FEATURE_ATTR] === 'string' && el[PENDO_FEATURE_ATTR]
    )
    if (fromElements) return fromElements[PENDO_FEATURE_ATTR]
  }

  const chain = event?.properties?.$elements_chain
  if (typeof chain !== 'string' || !chain) return null

  const fromChain = chain.match(PENDO_FEATURE_IN_CHAIN)
  return fromChain ? fromChain[1] : null
}

export const scrubEvent = (event) => {
  if (!event?.properties) return event

  const properties = { ...event.properties }

  Object.keys(properties).forEach((key) => {
    if (typeof properties[key] === 'string' && URL_PROPERTY.test(key)) {
      properties[key] = scrubIds(properties[key])
    }
  })

  // Allowlisted autocapture should only produce tagged Feature clicks. Drop anything
  // else, and promote the attr to a named event so it shows up like a Pendo Feature.
  if (event.event === '$autocapture') {
    const feature = getPendoFeature({ properties })
    if (!feature) return null

    properties.pendo_feature = feature
    return { ...event, event: feature, properties }
  }

  return { ...event, properties }
}

export const POSTHOG_CONFIG = {
  api_host: API_HOST,
  defaults: '2026-05-30',
  person_profiles: 'identified_only',
  autocapture: {
    css_selector_allowlist: [POSTHOG_FEATURE_SELECTOR],
    dom_event_allowlist: ['click'],
  },
  disable_session_recording: true,
  enable_heatmaps: false,
  before_send: scrubEvent,
}

let initialized = false
let pendingFlagCallbacks = []
let pendingFlagContext = null

// Replaces the previous context instead of merging into it, so a field that is
// gone from the new identity stops targeting flags.
const applyFlagContext = ({ companyId, environment, region } = {}) => {
  try {
    if (companyId) groupPosthogCompany(companyId)
    else posthog.resetGroups()

    const properties = {}
    if (environment !== undefined) properties.environment = environment
    if (region !== undefined) properties.region = region
    const hasProperties = Object.keys(properties).length > 0

    posthog.resetPersonPropertiesForFlags(!hasProperties)
    if (hasProperties) posthog.setPersonPropertiesForFlags(properties)
  } catch {} // eslint-disable-line no-empty
}

export function initPosthog(context = {}) {
  try {
    posthog.init(PROJECT_KEY, POSTHOG_CONFIG)
    initialized = true
  } catch {
    return // eslint-disable-line no-empty
  }

  try {
    posthog.register(posthogAppProperties(context))
  } catch {
    // Registration must not block flag subscriptions that follow.
  }

  if (pendingFlagContext) {
    applyFlagContext(pendingFlagContext)
    pendingFlagContext = null
  }

  pendingFlagCallbacks.forEach((entry) => {
    try {
      entry.unsubscribe = posthog.onFeatureFlags(entry.cb)
    } catch {} // eslint-disable-line no-empty
  })
  pendingFlagCallbacks = []
}

/**
 * Subscribes to PostHog flag evaluations. Queues the callback until
 * `initPosthog()` runs (never, on on-prem) and attaches it then.
 * @param {(flags: string[], variants: Record<string, boolean|string>) => void} cb
 * @returns {() => void} unsubscribe
 */
export function onPosthogFeatureFlags(cb) {
  if (initialized) {
    try {
      return posthog.onFeatureFlags(cb)
    } catch {
      return () => {} // eslint-disable-line no-empty
    }
  }

  const entry = { cb, unsubscribe: null }
  pendingFlagCallbacks.push(entry)

  return () => {
    pendingFlagCallbacks = pendingFlagCallbacks.filter((queued) => queued !== entry)
    try {
      entry.unsubscribe?.()
    } catch {} // eslint-disable-line no-empty
  }
}

/**
 * Sets the company group and person properties feature-flag evaluation targets
 * on. Does not call `identify` — that stays App.js's call. No-op before init;
 * the context is applied once `initPosthog()` runs.
 * @param {{ companyId?: string|number, environment?: string, region?: string }} context
 */
export function setPosthogFlagContext(context = {}) {
  if (!initialized) {
    pendingFlagContext = context
    return
  }

  applyFlagContext(context)
}

export function identifyPosthogUser(distinctId, personProperties = {}, superProperties = {}) {
  if (!initialized || !distinctId) return

  try {
    // Second arg is $set on the person (email/name for display). register() only
    // stamps event super-properties — it does not fill the person profile.
    posthog.identify(distinctId, personProperties)
    posthog.register(superProperties)
  } catch {} // eslint-disable-line no-empty
}

export function groupPosthogCompany(companyId, properties = {}) {
  if (!initialized || !companyId) return

  try {
    posthog.group('company', String(companyId), properties)
  } catch {} // eslint-disable-line no-empty
}

export function capturePosthogEvent(eventName, properties = {}) {
  if (!initialized || !eventName) return

  try {
    posthog.capture(eventName, properties)
  } catch {} // eslint-disable-line no-empty
}
